import { useEffect, useState } from "react";
import { convertHeicUrlToObjectUrl, getCachedHeicObjectUrl, isHeicFileName } from "src/Common/function";

/**
 * Returns a browser-displayable URL for an attachment. If the file is a HEIC/HEIF
 * image it is fetched and converted to a JPEG object URL; otherwise the original
 * URL is returned unchanged.
 *
 * The conversion result is cached at the module level (see `convertHeicUrlToObjectUrl`),
 * so calling this hook with the same `src` across components reuses prior work.
 *
 * @param {string} src - The original (possibly HEIC) URL.
 * @param {string} fileName - The file name, used to detect HEIC/HEIF by extension.
 * @returns {{ url: string, isHeic: boolean, isLoading: boolean, error: boolean }}
 */
export const useHeicImageUrl = (src, fileName) => {
  const needsConversion = isHeicFileName(fileName);

  const [state, setState] = useState(() => {
    if (!needsConversion || !src) {
      return { url: src || "", isHeic: needsConversion, isLoading: false, error: false };
    }
    const cached = getCachedHeicObjectUrl(src);
    return {
      url: cached || "",
      isHeic: true,
      isLoading: !cached,
      error: false,
    };
  });

  useEffect(() => {
    if (!needsConversion || !src) {
      setState({ url: src || "", isHeic: needsConversion, isLoading: false, error: false });
      return undefined;
    }

    const cached = getCachedHeicObjectUrl(src);
    if (cached) {
      setState({ url: cached, isHeic: true, isLoading: false, error: false });
      return undefined;
    }

    let cancelled = false;
    setState({ url: "", isHeic: true, isLoading: true, error: false });

    convertHeicUrlToObjectUrl(src)
      .then((url) => {
        if (cancelled) return;
        setState({ url, isHeic: true, isLoading: false, error: false });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to convert HEIC image:", error);
        setState({ url: "", isHeic: true, isLoading: false, error: true });
      });

    return () => {
      cancelled = true;
    };
  }, [needsConversion, src]);

  return state;
};

export default useHeicImageUrl;
